import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import LabelWithInputField from "./LabelWithInputField";
import D3EDate from "../classes/D3EDate";
import LabelWithDateField from "./LabelWithDateField";
import LabelWithDoubleInputField from "./LabelWithDoubleInputField";
import PageNavigator from "../classes/PageNavigator";
import Query from "../classes/Query";
import PopupWrapperView from "./PopupWrapperView";
import Button from "./Button";
import DBSaveStatus from "../utils/DBSaveStatus";
import SearchRequestsRequest from "../models/SearchRequestsRequest";
import ListWrapper from "../utils/ListWrapper";
import CheckServerErrors from "./CheckServerErrors";
import Invoice from "../models/Invoice";
import DateTime from "../core/DateTime";
import TextView from "./TextView";
import SearchRequest from "../models/SearchRequest";
import Hospital from "../models/Hospital";
import LabelDropdown from "./LabelDropdown";
import RPCServices from "../rpc/RPCServices";
import CollectionUtils from "../utils/CollectionUtils";
import DateUtils from "../classes/DateUtils";
import { Runnable } from "../classes/core";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

type _CancelButtonOnPressed = (d3eState: AddOrEditInvoiceViewRefs) => void;

type _LabelDropdownOnChanged = (
  text: Hospital,
  d3eState: AddOrEditInvoiceViewRefs
) => void;

type _LabelWithInputFieldOnChanged = (
  text: string,
  d3eState: AddOrEditInvoiceViewRefs
) => void;

type _LabelwithInputField4OnChanged = (
  text: number,
  d3eState: AddOrEditInvoiceViewRefs
) => void;

export interface AddOrEditInvoiceViewProps extends BaseUIProps {
  key?: string;
  obj: Invoice;
  requests: Array<SearchRequest>;
  _requestsHash?: number;
}
/// To store state data for AddOrEditInvoiceView
class AddOrEditInvoiceViewRefs {
  public cancelButton: CancelButtonState = new CancelButtonState();
  public saveButton: SaveButtonState = new SaveButtonState();
}

interface SaveButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AddOrEditInvoiceViewRefs;
}

class SaveButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _SaveButtonWithState extends ObservableComponent<SaveButtonWithStateProps> {
  saveButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SaveButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get saveButton(): SaveButtonState {
    return this.props.d3eState.saveButton;
  }
  public get d3eState(): AddOrEditInvoiceViewRefs {
    return this.props.d3eState;
  }
  public initState() {
    super.initState();

    this.updateObservable("saveButton", null, this.saveButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["saveButton", "saveButton.", "saveButton.hover"], this.rebuild);
  }
  public saveButtonOnEnter(event): void {
    return this.saveButton.setHover(true);
  }
  public saveButtonOnExit(event): void {
    return this.saveButton.setHover(false);
  }
  public dispose(): void {
    this.saveButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      width: 100,
      child: Button({
        padding: this.saveButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.saveButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.saveButton.disable,
        onFocusChange: (val) => {},
        child: TextView({ data: "Save" }),
        onEnter: (event) => {
          this.saveButtonOnEnter(event);
        },
        onExit: (event) => {
          this.saveButtonOnExit(event);
        },
      }),
      className: "x86 hc",
    });
  }
}
function SaveButtonWithState(props: SaveButtonWithStateProps) {
  return React.createElement(_SaveButtonWithState, props);
}

interface CancelButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AddOrEditInvoiceViewRefs;
  _onCancelHandler?: _CancelButtonOnPressed;
}

class CancelButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _CancelButtonWithState extends ObservableComponent<CancelButtonWithStateProps> {
  cancelButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: CancelButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get cancelButton(): CancelButtonState {
    return this.props.d3eState.cancelButton;
  }
  public get d3eState(): AddOrEditInvoiceViewRefs {
    return this.props.d3eState;
  }
  public get _onCancelHandler(): _CancelButtonOnPressed {
    return this.props._onCancelHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("cancelButton", null, this.cancelButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["cancelButton", "cancelButton.", "cancelButton.hover"],
      this.rebuild
    );
  }
  public cancelButtonOnEnter(event): void {
    return this.cancelButton.setHover(true);
  }
  public cancelButtonOnExit(event): void {
    return this.cancelButton.setHover(false);
  }
  public dispose(): void {
    this.cancelButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
      width: 100,
      child: Button({
        padding: this.cancelButton.hover
          ? cStyle.tButtonSecondaryPaddingOnHover
          : cStyle.tButtonSecondaryPaddingOn,
        decoration: this.cancelButton.hover
          ? cStyle.tButtonSecondaryDecorationOnHover
          : cStyle.tButtonSecondaryDecorationOn,
        disable: this.cancelButton.disable,
        onPressed: () => {
          this._onCancelHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Cancel" }),
        onEnter: (event) => {
          this.cancelButtonOnEnter(event);
        },
        onExit: (event) => {
          this.cancelButtonOnExit(event);
        },
      }),
      className: "xb4 hc",
    });
  }
}
function CancelButtonWithState(props: CancelButtonWithStateProps) {
  return React.createElement(_CancelButtonWithState, props);
}

class _AddOrEditInvoiceViewState extends ObservableComponent<AddOrEditInvoiceViewProps> {
  static defaultProps = { obj: null, requests: [] };
  d3eState: AddOrEditInvoiceViewRefs = new AddOrEditInvoiceViewRefs();
  errors: Array<string> = ListWrapper.widget(this, "errors");
  hospitals: Array<Hospital> = ListWrapper.widget(this, "hospitals");
  services: Array<SearchRequest> = ListWrapper.widget(this, "services");
  request: SearchRequestsRequest = null;
  dueDate: D3EDate = null;
  requestCount: Array<SearchRequest> = ListWrapper.widget(this, "requestCount");
  private _editInitListeners: Array<Runnable> = [];
  private _d3eModelErrors: Array<string> = [];
  private _titleErrors: Array<string> = [];
  public invoiceIdUniqueError: string;
  private _hospitalErrors: Array<string> = [];
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AddOrEditInvoiceViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): Invoice {
    return this.props.obj;
  }
  public get requests(): Array<SearchRequest> {
    return this.props.requests;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.subscribeToList(this.requests, "requests");

    this.updateSyncCollProperty("requests", this.props.requests);

    this.on(
      ["requests", "requests.hospital", "requests.status"],
      this.computeHospitals
    );

    this.computeHospitals();

    this.on(
      [
        "request",
        "request.applyStatus",
        "request.ascending",
        "request.createdBy",
        "request.fromDate",
        "request.hospital",
        "request.name",
        "request.offset",
        "request.orderBy",
        "request.pageSize",
        "request.request",
        "request.status",
        "request.toDate",
      ],
      this.computeServices
    );

    this.computeServices();

    this.on(
      ["obj", "obj.hospital", "services", "services.hospital"],
      this.computeRequestCount
    );

    this.computeRequestCount();

    this.on(
      [
        "dueDate",
        "errors",
        "hospitalErrors",
        "hospitals",
        "hospitals.name",
        "obj",
        "obj.amount",
        "obj.hospital",
        "obj.requestCount",
        "obj.title",
        "requestCount",
        "titleErrors",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: AddOrEditInvoiceViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }

    if (prevProps.requests !== this.props.requests) {
      this.updateObservableColl(
        "requests",
        prevProps.requests,
        this.props.requests
      );

      this.fire("requests", this);
    }
  }
  public setErrors(val: Array<string>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(this.errors, val);

    if (!isValChanged) {
      return;
    }

    this.errors.clear();

    this.errors.addAll(val);

    this.fire("errors", this);
  }
  public addToErrors(val: string, index: number = -1): void {
    if (index === -1) {
      if (!this.errors.contains(val)) this.errors.add(val);
    } else {
      this.errors.remove(this.errors.elementAt(index));

      this.errors.add(val);
    }

    this.fire("errors", this, val, true);
  }
  public removeFromErrors(val: string): void {
    this.errors.remove(val);

    this.fire("errors", this, val, false);
  }
  public setHospitals(val: Array<Hospital>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.hospitals,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("hospitals", this.hospitals, val);

    this.hospitals.clear();

    this.hospitals.addAll(val);

    this.fire("hospitals", this);
  }
  public addToHospitals(val: Hospital, index: number = -1): void {
    if (index === -1) {
      if (!this.hospitals.contains(val)) this.hospitals.add(val);
    } else {
      this.hospitals.remove(this.hospitals.elementAt(index));

      this.hospitals.add(val);
    }

    this.fire("hospitals", this, val, true);

    this.updateObservable("hospitals", null, val);
  }
  public removeFromHospitals(val: Hospital): void {
    this.hospitals.remove(val);

    this.fire("hospitals", this, val, false);

    this.removeObservable("hospitals", val);
  }
  public computeHospitals = (): void => {
    try {
      this.setHospitals(
        Array.from(DateUtils.getCompletedRequest(this.requests))
      );
    } catch (exception) {
      console.log(" exception in computeHospitals : " + exception.toString());

      this.setHospitals([]);
    }
  };
  public setServices(val: Array<SearchRequest>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(this.services, val);

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("services", this.services, val);

    this.services.clear();

    this.services.addAll(val);

    this.fire("services", this);
  }
  public addToServices(val: SearchRequest, index: number = -1): void {
    if (index === -1) {
      if (!this.services.contains(val)) this.services.add(val);
    } else {
      this.services.remove(this.services.elementAt(index));

      this.services.add(val);
    }

    this.fire("services", this, val, true);

    this.updateObservable("services", null, val);
  }
  public removeFromServices(val: SearchRequest): void {
    this.services.remove(val);

    this.fire("services", this, val, false);

    this.removeObservable("services", val);
  }
  public computeServices = async (): Promise<void> => {
    try {
      this.setServices(
        Array.from(
          (
            await Query.get().getSearchRequests(
              UsageConstants.QUERY_GETSEARCHREQUESTS_ADDOREDITINVOICEVIEW_PROPERTIES_SERVICES_COMPUTATION,
              this.request
            )
          )?.items ?? []
        )
      );
    } catch (exception) {
      console.log(" exception in computeServices : " + exception.toString());

      this.setServices([]);
    }
  };
  public setRequest(val: SearchRequestsRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setDueDate(val: D3EDate): void {
    let isValChanged: boolean = this.dueDate !== val;

    if (!isValChanged) {
      return;
    }

    this.dueDate = val;

    this.fire("dueDate", this);
  }
  public setRequestCount(val: Array<SearchRequest>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.requestCount,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("requestCount", this.requestCount, val);

    this.requestCount.clear();

    this.requestCount.addAll(val);

    this.fire("requestCount", this);
  }
  public addToRequestCount(val: SearchRequest, index: number = -1): void {
    if (index === -1) {
      if (!this.requestCount.contains(val)) this.requestCount.add(val);
    } else {
      this.requestCount.remove(this.requestCount.elementAt(index));

      this.requestCount.add(val);
    }

    this.fire("requestCount", this, val, true);

    this.updateObservable("requestCount", null, val);
  }
  public removeFromRequestCount(val: SearchRequest): void {
    this.requestCount.remove(val);

    this.fire("requestCount", this, val, false);

    this.removeObservable("requestCount", val);
  }
  public computeRequestCount = (): void => {
    try {
      this.setRequestCount(
        Array.from(
          this.services.where((s) => s.hospital === this.obj.hospital).toList()
        )
      );
    } catch (exception) {
      console.log(
        " exception in computeRequestCount : " + exception.toString()
      );

      this.setRequestCount([]);
    }
  };
  public get d3eModelErrors(): Array<string> {
    return this._d3eModelErrors ?? [];
  }
  public set d3eModelErrors(val: Array<string>) {
    this._d3eModelErrors.clear();

    this._d3eModelErrors.addAll(val);
  }
  public get titleErrors(): Array<string> {
    return this._titleErrors ?? [];
  }
  public set titleErrors(val: Array<string>) {
    this._titleErrors.clear();

    this._titleErrors.addAll(val);
  }
  public async computeTitleErrors(): Promise<void> {
    this.titleErrors = [];

    let it: string = this.obj.title;

    if (it == null || it === "") {
      this.titleErrors.add("Title cannot be empty.");
    }

    this.fire("titleErrors", this);
  }
  public async checkInvoiceInvoiceIdUniqueInHospital(): Promise<void> {
    if (this.obj == null || this.obj.hospital == null) {
      return;
    }

    let isUnique: boolean =
      await RPCServices.getUniqueChecker().checkInvoiceInvoiceIdUniqueInHospital(
        this.obj.hospital,
        this.obj,
        this.obj.invoiceId
      );

    if (!isUnique) {
      this.invoiceIdUniqueError = "Duplicate invoiceId found with this value.";
    }
  }
  public get hospitalErrors(): Array<string> {
    return this._hospitalErrors ?? [];
  }
  public set hospitalErrors(val: Array<string>) {
    this._hospitalErrors.clear();

    this._hospitalErrors.addAll(val);
  }
  public async computeHospitalErrors(): Promise<void> {
    this.hospitalErrors = [];

    let it: Hospital = this.obj.hospital;

    if (it == null) {
      this.hospitalErrors.add("Hospital cannot be null.");
    }

    this.fire("hospitalErrors", this);
  }
  public setEditDefaults(): void {}
  public get isEditValid(): boolean {
    if (this.titleErrors.isNotEmpty) {
      return false;
    }

    if (this.hospitalErrors.isNotEmpty) {
      return false;
    }

    return true;
  }
  public validate(): Array<string> {
    let errors: Array<string> = [];

    this.computeTitleErrors();

    errors.addAll(this.titleErrors);

    this.computeHospitalErrors();

    errors.addAll(this.hospitalErrors);

    return errors;
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return PopupWrapperView({
      title:
        this.obj.saveStatus !== DBSaveStatus.New
          ? "Edit Invoice"
          : "Add Invoice",
      content: ui.Column({
        crossAxisAlignment: ui.CrossAxisAlignment.start,
        mainAxisAlignment: ui.MainAxisAlignment.start,
        children: [
          ui.Row({
            mainAxisAlignment: ui.MainAxisAlignment.start,
            crossAxisAlignment: ui.CrossAxisAlignment.start,
            children: [
              ui.Container({
                margin: ui.EdgeInsets.zero,
                child: LabelDropdown<Hospital>({
                  placeHolder: "---Select---",
                  name: "Hospital",
                  value: this.obj.hospital,
                  items: this.hospitals,
                  width: 390,
                  isRequired: true,
                  errors: this.hospitalErrors,
                  onChanged: (text) => {
                    this.labelDropdownonChanged(text, this.d3eState);
                  },
                  builder: (context, item) => {
                    return TextView({
                      data: item === null ? "All" : item.name,
                    });
                  },
                }),
                key: "0",
              }),
              ui.Container({
                margin: ui.EdgeInsets.fromLTRB(10.0, 0.0, 0.0, 0.0, new Map()),
                width: 390,
                child: LabelWithInputField({
                  name: "Title",
                  placeHolder: "Enter Invoice Title",
                  isRequired: true,
                  value: this.obj.title,
                  errors: this.titleErrors,
                  onChanged: (text) => {
                    this.labelWithInputFieldonChanged(text, this.d3eState);
                  },
                }),
                key: "1",
                className: "xd41 hc",
              }),
            ],
            className: "xbb hc h",
            key: "0",
          }),
          ui.Row({
            mainAxisAlignment: ui.MainAxisAlignment.start,
            crossAxisAlignment: ui.CrossAxisAlignment.center,
            children: [
              ui.Container({
                margin: ui.EdgeInsets.zero,
                width: 395,
                child: LabelWithDateField({
                  name: "Due Date",
                  placeHolder: "Enter Due Date",
                  isRequired: true,
                  value: this.dueDate,
                  onChanged: (value) => {
                    this.onChangedLabelWithDateField2Handler(
                      value,
                      this.d3eState
                    );
                  },
                }),
                key: "0",
                className: "xb9 hc",
              }),
              ui.Container({
                margin: ui.EdgeInsets.fromLTRB(15.0, 0.0, 0.0, 0.0, new Map()),
                child: LabelWithDoubleInputField({
                  name: "Amount",
                  placeHolder: "Enter Amount",
                  isRequired: true,
                  value: this.obj.amount,
                  width: 385,
                  onChanged: (text) => {
                    this.labelwithInputField4onChanged(text, this.d3eState);
                  },
                }),
                key: "1",
                className: "x48",
              }),
            ],
            className: "xbe hc h",
            key: "1",
          }),
          ui.Row({
            mainAxisAlignment: ui.MainAxisAlignment.start,
            crossAxisAlignment: ui.CrossAxisAlignment.start,
            children: [
              this.requestCount.isNotEmpty
                ? TextView({
                    data: this.requestCount.length.toString() + " Requests",
                    style: new ui.TextStyle({
                      fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                      fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
                    }),
                    className: "x2d",
                  })
                : this.obj.requestCount > 0
                ? TextView({
                    data: this.obj.requestCount.toString() + " Requests",
                    style: new ui.TextStyle({
                      fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
                      fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
                    }),
                    className: "x66",
                  })
                : [],
            ],
            className: "xdc hc h",
            key: "2",
          }),
          this.errors.isNotEmpty
            ? CheckServerErrors({ errors: this.errors, className: "hc" })
            : [],
        ],
        className: "x2fa hc h",
      }),
      buttons: [
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.start,
              children: [
                CancelButtonWithState({
                  d3eState: this.d3eState,
                  _onCancelHandler: this.onCancelHandler,
                  key: "0",
                }),
                SaveButtonWithState({ d3eState: this.d3eState, key: "1" }),
              ],
              key: "0",
            }),
          ],
          key: "0",
        }),
      ],
      className: ui.join(this.props.className, "AddOrEditInvoiceView hc vc"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.obj.setInvoiceDate(DateTime.now());
  };
  public onCancelHandler = (d3eState: AddOrEditInvoiceViewRefs): void => {
    this.obj.restore();

    this.navigator.close();
  };
  public onChangedLabelWithDateField2Handler = (
    value: D3EDate,
    d3eState: AddOrEditInvoiceViewRefs
  ): void => {
    this.setDueDate(value);

    if (value !== null) {
      this.obj.setDueDate(value.toDateTime());
    }
  };
  public labelDropdownonChanged = (
    val: Hospital,
    d3eState: AddOrEditInvoiceViewRefs
  ): void => {
    this.obj.setHospital(val);
  };
  public labelWithInputFieldonChanged = (
    val: string,
    d3eState: AddOrEditInvoiceViewRefs
  ): void => {
    this.obj.setTitle(val);
  };
  public labelwithInputField4onChanged = (
    val: number,
    d3eState: AddOrEditInvoiceViewRefs
  ): void => {
    this.obj.setAmount(val);
  };
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get cancelButton() {
    return this.d3eState.cancelButton;
  }
  public get saveButton() {
    return this.d3eState.saveButton;
  }
}
export default function AddOrEditInvoiceView(props: AddOrEditInvoiceViewProps) {
  return React.createElement(
    _AddOrEditInvoiceViewState,
    { ..._AddOrEditInvoiceViewState.defaultProps, ...props },
    ListWrapper.fromInput<SearchRequest>(props.requests, "requests")
  );
}
